<script setup lang="ts">
import {
  AccountRouteName,
  AuthRouteName,
} from '@/js/account/app/router/route-names';
import BaseIcon from '@/js/account/components/base/BaseIcon.vue';
import { PROFILE_NAV_ITEMS } from '@/js/account/components/profile/profile-nav-items';
import { signOut } from '@/js/account/entities/auth';
import { useAuth } from '@/js/account/entities/session';
import { classNames } from '@/js/lib/bem';
import { computed, ref } from 'vue';
import { RouterLink, useRouter } from 'vue-router';

const cn = classNames('profile-sidebar');

const router = useRouter();
const { user } = useAuth();

const loggingOut = ref(false);

const displayName = computed(() => user.value?.name ?? user.value?.email ?? '');

const avatarUrl = computed(() => {
  const image = user.value?.avatar.large ?? user.value?.avatar.small;

  return image?.url2x ?? image?.url ?? null;
});

async function onLogout(): Promise<void> {
  if (loggingOut.value) {
    return;
  }

  loggingOut.value = true;

  try {
    await signOut();
    await router.push({ name: AuthRouteName.Login });
  } finally {
    loggingOut.value = false;
  }
}
</script>

<template>
  <aside :class="cn()" aria-label="Account navigation">
    <a href="/" :class="cn('logo-link')" aria-label="Home">
      <BaseIcon name="logo" :class="cn('logo')" :width="113" :height="48" />
    </a>
    <ul :class="cn('items')">
      <li
        v-for="item in PROFILE_NAV_ITEMS"
        :key="item.routeName"
        :class="cn('item')"
      >
        <RouterLink
          :to="{ name: item.routeName }"
          :class="cn('item-link')"
          :active-class="cn('item-link', { active: true })"
        >
          <BaseIcon
            :name="item.icon"
            :class="cn('item-icon')"
            :width="24"
            :height="24"
          />
          <div :class="cn('item-text')">
            {{ item.label }}
          </div>
        </RouterLink>
      </li>
    </ul>
    <RouterLink
      v-if="user"
      :to="{ name: AccountRouteName.Profile }"
      :class="cn('user')"
    >
      <div :class="cn('user-icon')">
        <img
          v-if="avatarUrl"
          :src="avatarUrl"
          alt=""
          :class="cn('user-info-image')"
        />
        <p v-else :class="cn('user-initials')">
          {{ displayName.charAt(0).toUpperCase() }}
        </p>
      </div>
      <div :class="cn('user-name')">
        {{ displayName }}
      </div>
    </RouterLink>
    <button
      type="button"
      :class="cn('logout')"
      :disabled="loggingOut"
      @click="onLogout"
    >
      <BaseIcon
        name="logout"
        :class="cn('logout-icon')"
        :width="20"
        :height="20"
      />
      <div :class="cn('logout-text')">Logout</div>
    </button>
  </aside>
</template>

<style lang="scss" scoped>
.profile-sidebar {
  padding: 16px;
  box-shadow: var(--shadow);
  height: 100%;
  display: flex;
  flex-direction: column;
  position: fixed;
  font-family: var(--font-family-sans);
  z-index: 10;
  background-color: var(--color-white);

  &__logo-link {
    display: inline-flex;
    margin-bottom: 40px;
    text-decoration: none;
    color: inherit;
  }

  &__logo {
    display: block;
  }

  &__items {
    flex: 1;
    margin: 0 0 32px;
    list-style: none;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__item-link {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border-radius: 8px;
    transition:
      background-color 0.2s ease-in-out,
      color 0.2s ease-in-out;
    text-decoration: none;
    color: var(--color-accent);

    &:hover {
      background-color: var(--color-background-secondary);
      color: var(--color-primary);
    }

    &_active {
      background-color: var(--color-background-secondary);
      color: var(--color-primary);
    }
  }

  &__user {
    margin-bottom: 32px;
    display: flex;
    align-items: center;
    gap: 10px;
    text-decoration: none;
    color: var(--color-text-primary);
  }

  &__user-initials {
    font-weight: var(--font-weight-regular);
    font-size: 16px;
    line-height: 1;
    color: var(--color-white);
    margin: 0;
  }

  &__user-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    overflow: hidden;
    background-color: var(--color-primary);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__user-info-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__logout {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    background-color: transparent;
    border: none;
    cursor: pointer;
    color: var(--color-text-accent);
    transition: color 0.2s ease-in-out;

    &:hover {
      color: var(--color-primary);
    }
  }
}
</style>
